import { useMemo } from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PipelineStep {
  id?: string;
  label: string;
  pendingText?: string;
  activeText?: string;
  doneText?: string;
  errorText?: string;
  duration?: string;
}

export interface PipelineStepsProps {
  steps: PipelineStep[];
  current: number;
  error?: { step: number; message: string };
  variant?: 'compact' | 'comfortable';
  showNumbers?: boolean;
}

type StepStatus = 'done' | 'active' | 'pending' | 'error';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// ─── PipelineSteps ────────────────────────────────────────────────────────────

const PipelineSteps = ({
  steps,
  current,
  error,
  variant = 'comfortable',
  showNumbers = true,
}: PipelineStepsProps) => {
  const reduced = useMemo(prefersReducedMotion, []);
  const nodeSize = variant === 'compact' ? 20 : 24;

  const getStatus = (i: number): StepStatus => {
    if (error && i === error.step) return 'error';
    if (error && i > error.step) return 'pending';
    if (i < current) return 'done';
    if (i === current) return 'active';
    return 'pending';
  };

  return (
    <ol className="pipeline-steps" role="list">
      {steps.map((step, i) => {
        const status = getStatus(i);
        const isLast = i === steps.length - 1;
        const key = step.id ?? `${i}-${step.label}`;
        const connectorFilled = i < current && (!error || i < error.step);

        const subText =
          status === 'done'
            ? step.doneText
            : status === 'active'
            ? step.activeText
            : status === 'error'
            ? (step.errorText ?? error?.message)
            : step.pendingText;

        return (
          <li
            key={key}
            className={`pipeline-step pipeline-step--${status}`}
            aria-label={`Step ${i + 1} of ${steps.length}, ${status}`}
            aria-current={status === 'active' ? 'step' : undefined}
          >
            {/* Left column: node + vertical connector */}
            <div className="pipeline-step__track">
              <StatusNode
                status={status}
                size={nodeSize}
                number={i + 1}
                showNumber={showNumbers}
                reducedMotion={reduced}
              />
              {!isLast && (
                <div
                  className={`pipeline-step__connector${connectorFilled ? ' pipeline-step__connector--filled' : ''}`}
                />
              )}
            </div>

            {/* Right column: label + sub-text */}
            <div className="pipeline-step__body">
              <div className="pipeline-step__header">
                <span className={`pipeline-step__label pipeline-step__label--${status}`}>
                  {step.label}
                </span>
                <div className="pipeline-step__meta">
                  {status === 'done' && step.duration && (
                    <span className="pipeline-step__duration">{step.duration}</span>
                  )}
                  {status === 'active' && (
                    <span
                      className={`pipeline-step__spinner${reduced ? '' : ' pipeline-step__spinner--animated'}`}
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>

              {subText && (
                <p className={`pipeline-step__subtext pipeline-step__subtext--${status}`}>
                  {subText}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

// ─── StatusNode ───────────────────────────────────────────────────────────────

interface StatusNodeProps {
  status: StepStatus;
  size: number;
  number: number;
  showNumber: boolean;
  reducedMotion: boolean;
}

const StatusNode = ({ status, size, number, showNumber, reducedMotion }: StatusNodeProps) => {
  const style: React.CSSProperties = { width: size, height: size, minWidth: size };
  const iconSize = Math.max(9, Math.round(size * 0.5));
  const dotSize = Math.max(6, Math.round(size * 0.34));

  if (status === 'done') {
    return (
      <div className="pipeline-node pipeline-node--done" style={style}>
        <CheckOutlined style={{ fontSize: iconSize }} />
      </div>
    );
  }

  if (status === 'active') {
    return (
      <div className="pipeline-node pipeline-node--active" style={style}>
        <span
          className={`pipeline-node__pulse${reducedMotion ? '' : ' pipeline-node__pulse--animated'}`}
          style={{ width: dotSize, height: dotSize }}
        />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="pipeline-node pipeline-node--error" style={style}>
        <CloseOutlined style={{ fontSize: iconSize }} />
      </div>
    );
  }

  // pending
  return (
    <div
      className="pipeline-node pipeline-node--pending"
      style={{ ...style, fontSize: Math.max(9, Math.round(size * 0.42)) }}
    >
      {showNumber ? number : ''}
    </div>
  );
};

export default PipelineSteps;
